// ======================================
// Projects Page Module
// ======================================

import { icons, renderProjectCard } from '../components.js';
import { portfolioAPI } from '../lib/api.js';

let allProjects = [];

export function renderProjectsPage() {
  return `
    <section class="page-header">
      <div class="container">
        <div class="page-header-content">
          <h1 class="page-title">Our Projects</h1>
          <p class="page-subtitle">
            Explore our portfolio of completed projects. Real homes, real transformations.
          </p>
        </div>
      </div>
    </section>

    <section class="section projects-section">
      <div class="container">
        <div class="filter-bar">
          <button class="filter-btn active" data-filter="all">All Projects</button>
          <button class="filter-btn" data-filter="1 BHK">1 BHK</button>
          <button class="filter-btn" data-filter="2 BHK">2 BHK</button>
          <button class="filter-btn" data-filter="3 BHK">3 BHK</button>
          <button class="filter-btn" data-filter="Villa">Villa</button>
        </div>
        
        <div class="projects-grid" id="projects-grid">
           <div class="loading-spinner">Loading Projects...</div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Want Your Home Featured Here?</h2>
          <p class="cta-subtitle">
            Start your interior transformation today.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Book Free Consultation
          </button>
        </div>
      </div>
    </section>
  `;
}

export function initProjectsPage() {
  loadProjects();
  initFilters();
}

async function loadProjects() {
  const grid = document.getElementById('projects-grid');
  if (!grid) return;

  try {
    // Use portfolioAPI for public showcase
    const response = await portfolioAPI.list();
    const items = Array.isArray(response) ? response : (response.data || []);

    // Map portfolio fields to project card props
    allProjects = items.map(p => ({
      ...p,
      image: p.featured_image || p.image,
      location: p.city || p.location,
      bhk: p.category, // or p.bhk if available
      total_amount: p.budget_range || p.total_amount,
      duration: p.duration_days ? `${p.duration_days} Days` : p.duration
    }));

    if (allProjects.length) {
      renderGrid(allProjects);
    } else {
      grid.innerHTML = '<p class="text-center text-muted" style="grid-column:1/-1;">No projects found.</p>';
    }
  } catch (e) {
    console.error('Failed to load projects:', e);
    grid.innerHTML = '<p class="text-center text-error" style="grid-column:1/-1;">Failed to load projects.</p>';
  }
}

function renderGrid(items) {
  const grid = document.getElementById('projects-grid');
  if (grid) {
    grid.innerHTML = items.map(project => renderProjectCard(project)).join('');
  }
}

function initFilters() {
  document.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      // Update active state
      document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      // Filter projects
      const filter = btn.dataset.filter;

      let filteredProjects = allProjects;
      if (filter !== 'all') {
        filteredProjects = allProjects.filter(p => p.bhk && p.bhk.includes(filter)); // safe check
      }

      renderGrid(filteredProjects);
    });
  });
}

// Project Detail Page
export function renderProjectDetailPage(params) {
  // Skeleton / Loading State
  return `
    <div id="project-detail-container">
        <section class="page-header">
            <div class="container">
                <div class="page-header-content">
                    <h1 class="page-title">Loading Project...</h1>
                </div>
            </div>
        </section>
    </div>
  `;
}

export async function initProjectDetailPage(params) {
  const container = document.getElementById('project-detail-container');
  if (!container) return;

  try {
    // We need an API to get project by slug. 
    // If API only supports ID, we might need to list all and find, or update API.
    // For now, assume list and find. Or projectsAPI.get(slug) if backend supports it.
    // Backend usually expects ID. Ideally backend adds getBySlug. 
    // I'll try to fetch all (cached usually) and find.

    let project = null;

    // Try to get from local cache first
    if (allProjects.length > 0) {
      project = allProjects.find(p => p.slug === params.slug);
    }

    // Fetch from API if not found
    if (!project) {
      try {
        const result = await portfolioAPI.get(params.slug);
        project = result.data || result;

        // Map fields if fetched individually
        if (project) {
          project.image = project.featured_image || project.image;
          project.location = project.city || project.location;
          project.bhk = project.category;
          project.total_amount = project.budget_range || project.total_amount;
          project.duration = project.duration_days ? `${project.duration_days} Days` : project.duration;
        }
      } catch (err) {
        console.warn('Project fetch failed', err);
      }
    }

    if (!project) {
      container.innerHTML = `
              <section class="page-header">
                <div class="container">
                  <div class="page-header-content">
                    <h1 class="page-title">Project Not Found</h1>
                    <p class="page-subtitle">The project you're looking for doesn't exist.</p>
                    <a href="/projects" class="btn btn-primary mt-6">View All Projects</a>
                  </div>
                </div>
              </section>
            `;
      return;
    }

    // Render Project
    renderProjectDetailContent(container, project);
    initBeforeAfter();

  } catch (e) {
    console.error('Project Detail Error:', e);
    container.innerHTML = '<div class="container p-8 text-center">Failed to load project details.</div>';
  }
}

function renderProjectDetailContent(container, project) {
  // Helper to safely parse rooms if string
  let rooms = project.rooms;
  if (typeof rooms === 'string') {
    try { rooms = JSON.parse(rooms); } catch (e) { rooms = []; }
  }
  if (!Array.isArray(rooms)) rooms = [];

  // Filter other projects
  // Note: this implementation requires fetching other projects again or passing them.
  // For simplicity, we might skip "More Projects" or fetch them.

  container.innerHTML = `
    <section class="section project-detail-hero">
      <div class="container">
        <div class="project-detail-header">
          <span class="project-detail-badge">
            ${icons.home}
            ${project.bhk || 'Home'}
          </span>
          <h1 class="project-detail-title">${project.title}</h1>
          <div class="project-detail-meta">
            <span class="project-meta-item">
              ${icons.mapPin}
              ${project.location || 'Chennai'}
            </span>
            <span class="project-meta-item">
              ${icons.calculator}
              ${project.total_amount ? '₹' + project.total_amount : 'TBD'}
            </span>
            <span class="project-meta-item">
              ${icons.clock}
              ${// Calculate duration or use field if exists. 
    project.duration || '45 Days'
    }
            </span>
          </div>
        </div>
        
        <!-- Before/After Comparison - Only if before/after images exist -->
        ${project.before_image && project.after_image ? `
        <div class="before-after" id="before-after">
          <img src="${project.after_image}" alt="After" class="before-after-img">
          <div class="before-after-after" style="width: 50%;">
            <img src="${project.before_image}" alt="Before">
          </div>
          <div class="before-after-slider" style="left: 50%;">
            <div class="before-after-handle">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M8 9l-3 3 3 3M16 9l3 3-3 3"/>
              </svg>
            </div>
          </div>
          <span class="before-after-label before-label">Before</span>
          <span class="before-after-label after-label">After</span>
        </div>
        ` : `
           <img src="${project.image || project.after_image}" alt="${project.title}" style="width:100%; border-radius: var(--radius-lg); margin-top: var(--space-8);">
        `}
      </div>
    </section>

    <!-- Project Info Section -->
    <section class="section project-info-section">
      <div class="container">
        <div class="project-info-grid">
          <div class="project-story">
            <h3>The Story</h3>
            <p>${project.description || 'No description available.'}</p>
          </div>
          
          <div class="project-sidebar">
            <div class="project-sidebar-card">
              <h4 class="project-sidebar-title">Project Details</h4>
              <div class="project-sidebar-list">
                <div class="project-sidebar-item">
                  <span class="project-sidebar-label">Property Type</span>
                  <span class="project-sidebar-value">${project.property_type || project.bhk}</span>
                </div>
                <div class="project-sidebar-item">
                  <span class="project-sidebar-label">Location</span>
                  <span class="project-sidebar-value">${project.city || project.location}</span>
                </div>
              </div>
            </div>
            
            <div class="project-sidebar-card">
              <h4 class="project-sidebar-title">Rooms Designed</h4>
              <div class="project-sidebar-list">
                ${rooms.map(room => `
                  <div class="project-sidebar-item">
                    <span class="project-sidebar-label">${icons.check}</span>
                    <span class="project-sidebar-value">${room}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Love This Design?</h2>
          <p class="cta-subtitle">
            Let us create something similar for your home.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Book Free Consultation
          </button>
        </div>
      </div>
    </section>
    `;
}

// Before/After slider functionality
export function initBeforeAfter() {
  const container = document.getElementById('before-after');
  if (!container) return;

  const slider = container.querySelector('.before-after-slider');
  const afterDiv = container.querySelector('.before-after-after');
  let isDragging = false;

  function updatePosition(x) {
    const rect = container.getBoundingClientRect();
    let percentage = ((x - rect.left) / rect.width) * 100;
    percentage = Math.max(0, Math.min(100, percentage));

    slider.style.left = `${percentage}%`;
    afterDiv.style.width = `${percentage}%`;
  }

  container.addEventListener('mousedown', (e) => {
    isDragging = true;
    updatePosition(e.clientX);
  });

  document.addEventListener('mousemove', (e) => {
    if (isDragging) {
      updatePosition(e.clientX);
    }
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
  });

  // Touch support
  container.addEventListener('touchstart', (e) => {
    isDragging = true;
    updatePosition(e.touches[0].clientX);
  });

  container.addEventListener('touchmove', (e) => {
    if (isDragging) {
      updatePosition(e.touches[0].clientX);
    }
  });

  container.addEventListener('touchend', () => {
    isDragging = false;
  });
}
